import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useDashboardToast } from '@/composables/useDashboardToast'
import {
  EmailVerificationRequestError,
  toEmailVerificationRequestError,
} from '@/helpers/emailAccountErrors'

export type EmailVerificationRequestResult = {
  email: string
  devVerificationUrl?: string
}

export {
  EMAIL_ACCOUNT_ERROR_CODES,
  EmailVerificationRequestError,
  parseEmailAccountApiError,
  toEmailVerificationRequestError,
} from '@/helpers/emailAccountErrors'

/**
 * Request a verification email before an address is linked (POST /me/accounts).
 */
export function useRequestEmailVerification() {
  const store = useStore()
  const { t } = useI18n()
  const { showToast } = useDashboardToast()
  const isSubmitting = ref(false)

  const requestVerification = async (
    email: string
  ): Promise<EmailVerificationRequestResult> => {
    isSubmitting.value = true
    try {
      const response = await store.dispatch('accounts/postAccount', {
        id: email,
        type: store.state.accounts.accountTypeConstant.email,
      })
      const data = response?.data ?? {}
      return {
        email: data.id ?? email,
        devVerificationUrl: data.devVerificationUrl,
      }
    } catch (error) {
      const mapped = toEmailVerificationRequestError(error)
      if (mapped) {
        throw mapped
      }
      throw error
    } finally {
      isSubmitting.value = false
    }
  }

  const resendVerification = async (email: string) => {
    const result = await requestVerification(email)
    showToast(t('Dashboard.Status.Emails.Pending.ResentToast', { email: result.email }))
    return result
  }

  const cancelVerification = async (email: string) => {
    await store.dispatch('accounts/cancelPendingEmailVerification', email)
    showToast(t('Dashboard.Status.Emails.Pending.CancelledToast', { email }))
  }

  return {
    isSubmitting,
    requestVerification,
    resendVerification,
    cancelVerification,
  }
}
